import React, { useMemo, useState, useCallback, useRef } from 'react';
import { DataTable } from '../components/organisms';
import { Button } from '../components/atoms';
import { useGenres, useGenreActions } from '../hooks/useGenres';
import type { Column, TableAction } from '../types/table';
import type { Genre, GenreCreateRequest, GenreUpdateRequest } from '../types/api/genre';
import toast from 'react-hot-toast';
import { DeleteConfirmationModal } from '../components/organisms/DeleteConfirmationModal';
import { GenreFormModal } from '../components/organisms/GenreFormModal';

export const GenreManagementPage: React.FC = () => {
  const { fetchGenres } = useGenres();
  const { createGenre, updateGenre, deleteGenre } = useGenreActions();
  const refreshKeyRef = useRef(0);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedGenre, setSelectedGenre] = useState<Genre | null>(null);

  const handleRefresh = useCallback(() => {
    refreshKeyRef.current += 1;
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedGenre(null);
    setIsCreateModalOpen(true);
  }, []);

  const handleEdit = useCallback((genre: Genre) => {
    setSelectedGenre(genre);
    setIsEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((genre: Genre) => {
    setSelectedGenre(genre);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCreateSubmit = useCallback(async (payload: GenreCreateRequest, iconFile: File | null) => {
    try {
      await createGenre({ data: payload, iconFile });
      toast.success('Genre created successfully');
      setIsCreateModalOpen(false);
      handleRefresh();
    } catch (e: any) {
      toast.error(e.message || 'Failed to create genre');
    }
  }, [createGenre, handleRefresh]);

  const handleEditSubmit = useCallback(async (payload: GenreUpdateRequest, iconFile: File | null) => {
    if (!selectedGenre) return;
    try {
      await updateGenre(selectedGenre.genreId, { data: payload, iconFile });
      toast.success('Genre updated successfully');
      setIsEditModalOpen(false);
      setSelectedGenre(null);
      handleRefresh();
    } catch (e: any) {
      toast.error(e.message || 'Failed to update genre');
    }
  }, [selectedGenre, updateGenre, handleRefresh]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedGenre) return;
    try {
      await deleteGenre(selectedGenre.genreId);
      toast.success('Genre deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedGenre(null);
      handleRefresh();
    } catch (e: any) {
      toast.error(e.message || 'Failed to delete genre');
    }
  }, [selectedGenre, deleteGenre, handleRefresh]);

  const columns = useMemo<Column<Genre>[]>(() => ([
    {
      id: 'name',
      header: 'Genre',
      accessor: 'name',
      cell: (row) => (
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold flex-shrink-0"
            style={{ backgroundColor: row.colorCode }}
          >
            {row.iconUrl ? (
              <img src={row.iconUrl} alt={row.name} className="w-full h-full object-cover rounded-lg" />
            ) : (
              <span>{row.name.charAt(0)}</span>
            )}
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">{row.name}</p>
            <p className="text-xs text-gray-500">{row.nameMm}</p>
          </div>
        </div>
      ),
      minWidth: '200px',
    },
    {
      id: 'description',
      header: 'Description',
      accessor: 'description',
      cell: (row) => (
        <span className="text-sm text-gray-700 line-clamp-1">{row.description}</span>
      ),
      hideOnMobile: true,
    },
    {
      id: 'isActive',
      header: 'Status',
      accessor: 'isActive',
      cell: (row) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${row.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      id: 'contentCount',
      header: 'Content',
      accessor: 'contentCount',
      cell: (row) => (
        <span className="text-sm text-gray-900">{row.contentCount}</span>
      ),
      hideOnMobile: true,
    },
  ]), []);

  const actions = useMemo<TableAction<Genre>[]>(() => ([
    {
      id: 'edit',
      label: 'Edit',
      variant: 'ghost',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
        </svg>
      ),
      onClick: handleEdit,
    },
    {
      id: 'delete',
      label: 'Delete',
      variant: 'danger',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      ),
      onClick: handleDelete,
    },
  ]), [handleEdit, handleDelete]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-900">Genre Management</h1>
          <p className="text-gray-600 mt-1 text-left pl-3">Manage content genres</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleCreate} leftIcon={
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
          }>Add Genre</Button>
        </div>
      </div>

      <DataTable<Genre>
        columns={columns}
        fetchData={fetchGenres}
        searchable
        searchPlaceholder="Search by name..."
        initialPageSize={10}
        pageSizeOptions={[10, 20, 50]}
        actions={actions}
        hoverable
        showRefreshButton
        onRefresh={handleRefresh}
        key={refreshKeyRef.current}
        caption="Genre management table"
      />

      <GenreFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={async ({ payload, iconFile }) => {
          await handleCreateSubmit(payload, iconFile);
        }}
        mode="create"
      />

      <GenreFormModal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setSelectedGenre(null); }}
        onSubmit={async ({ payload, iconFile }) => {
          await handleEditSubmit(payload, iconFile);
        }}
        mode="edit"
        genre={selectedGenre || undefined}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setSelectedGenre(null); }}
        onConfirm={handleDeleteConfirm}
        title="Delete Genre"
        message={selectedGenre ? `Are you sure you want to delete "${selectedGenre.name}"?` : 'Are you sure you want to delete this genre?'}
      />
    </div>
  );
};

export default GenreManagementPage;
